import { queryKeys } from "@/lib/query-keys";
import type { SupportChat, Message } from "@/types";

// Track the current viewing chat globally
let currentViewingChatId: string | null = null;

// Map to track unread counts for each chat to prevent overwrites
const chatUnreadCounts = new Map<string, number>();

// Set the currently viewing chat ID
export const setCurrentViewingChat = (chatId: string | null) => {
  currentViewingChatId = chatId;
};

// Get the currently viewing chat ID
export const getCurrentViewingChat = () => currentViewingChatId;

// Initialize unread counts from chat list
export const initializeChatUnreadCounts = (chats: SupportChat[]) => {
  chats.forEach((chat) => {
    if (!chatUnreadCounts.has(chat.id)) {
      chatUnreadCounts.set(chat.id, chat.unread_count);
    }
  });
};

// Update unread count for a specific chat
export const updateChatUnreadCount = (
  chatId: string,
  increment: boolean = true
) => {
  const currentCount = chatUnreadCounts.get(chatId) || 0;

  if (chatId === currentViewingChatId) {
    // If this is the currently viewing chat, always set to 0
    chatUnreadCounts.set(chatId, 0);
  } else if (increment) {
    // If not currently viewing and we need to increment
    chatUnreadCounts.set(chatId, Math.max(0, currentCount + 1));
  }

  return chatUnreadCounts.get(chatId) || 0;
};

// Reset unread count for a specific chat
export const resetChatUnreadCount = (chatId: string) => {
  chatUnreadCounts.set(chatId, 0);
  return 0;
};

const updateChatListData = (
  oldData: any,
  updateChat: (chat: SupportChat) => SupportChat
) => {
  if (oldData?.pages) {
    return {
      ...oldData,
      pages: oldData.pages.map((page: any) => ({
        ...page,
        results: page.results.map(updateChat),
      })),
    };
  }

  if (oldData?.results) {
    return {
      ...oldData,
      results: oldData.results.map(updateChat),
    };
  }

  return oldData;
};

// Optimistic cache update for real-time messages
export const updateChatListCacheOptimistic = (
  queryClient: any,
  chatId: string,
  newMessage?: any,
  forceUnreadCountUpdate: boolean = false
) => {
  queryClient.setQueriesData(
    { queryKey: queryKeys.ping.chats.all() },
    (oldData: any) =>
      updateChatListData(oldData, (chat) => {
        if (chat.id === chatId) {
          // Calculate new unread count based on current state
          let newUnreadCount = chat.unread_count;

          if (newMessage && newMessage.is_from_user) {
            // New message from user - update unread count
            newUnreadCount = updateChatUnreadCount(chatId, true);
          } else if (
            chatId === currentViewingChatId ||
            forceUnreadCountUpdate
          ) {
            // Currently viewing this chat or force update - set to 0
            newUnreadCount = resetChatUnreadCount(chatId);
          } else {
            // Use cached unread count to prevent overwrites
            newUnreadCount = chatUnreadCounts.get(chatId) ?? chat.unread_count;
          }

          const updatedChat = {
            ...chat,
            unread_count: newUnreadCount,
            updated_at: newMessage ? newMessage.created_at : chat.updated_at,
          };

          // Update last message if we have new message data
          if (newMessage) {
            updatedChat.last_message = {
              id: newMessage.message_id || newMessage.id,
              text_content: newMessage.text_content,
              message_type: newMessage.message_type,
              created_at: newMessage.created_at,
              is_from_user: newMessage.is_from_user,
              sender_name: newMessage.sender?.name || "Unknown",
            } as Message;

            // Update timestamp fields
            if (newMessage.is_from_user) {
              updatedChat.last_user_message_at = newMessage.created_at;
            } else {
              updatedChat.last_care_manager_message_at = newMessage.created_at;
            }
          }

          // Update our internal cache
          chatUnreadCounts.set(chatId, newUnreadCount);

          return updatedChat;
        }
        return chat;
      })
  );
};

// Mark all messages in a chat as read
export const markChatAsReadInCache = (queryClient: any, chatId: string) => {
  // Reset internal counter
  resetChatUnreadCount(chatId);

  // Update cache
  updateChatListCacheOptimistic(queryClient, chatId, undefined, true);
};

// Sync with server data to ensure consistency
export const syncChatListWithServer = (
  queryClient: any,
  serverChats: SupportChat[]
) => {
  // Update our internal cache with server data for non-viewing chats
  serverChats.forEach((chat) => {
    if (chat.id !== currentViewingChatId) {
      chatUnreadCounts.set(chat.id, chat.unread_count);
    }
  });

  // Update the query cache
  queryClient.setQueriesData(
    { queryKey: queryKeys.ping.chats.all() },
    (oldData: any) =>
      updateChatListData(oldData, (chat) => {
        const serverChat = serverChats.find((sc) => sc.id === chat.id);
        if (serverChat) {
          // Use cached unread count for currently viewing chat, server data for others
          const finalUnreadCount =
            chat.id === currentViewingChatId
              ? 0
              : (chatUnreadCounts.get(chat.id) ?? serverChat.unread_count);

          return {
            ...serverChat,
            unread_count: finalUnreadCount,
          };
        }
        return chat;
      })
  );
};
